import { GridPagerSettings, GridRowProps } from '@progress/kendo-react-grid';
import { cloneElement, FC, ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useSupplierColumns from 'src/assets/static/manage-suppliers/useSupplierColumns';
import GridWrapper, { GridColumn } from 'src/components/GridWrapper';
import { MANAGE_SUPPLIERS_LAPTOP_MIN_WIDTH_PX } from 'src/constants/manageSuppliers';
import useWindowSize from 'src/hooks/useWindowSize';
import { SuppliersContext } from 'src/sections/manage_suppliers/SuppliersContext';
import { ManageSupplier } from 'src/types/manage-suppliers/ManageSupplier';
import { useContextSelector } from 'use-context-selector';
import SuppliersToolbarActions from './SuppliersToolbarActions';

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 25, 50, 100],
};

const defaultProps = {
  tableTextId: 'Suppliers',
  DataItemKey: 'rowId',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  pageable: gridPagerSettings,
} as const;

const SuppliersTable: FC = () => {
  const { t } = useTranslation();
  const suppliers = useContextSelector(SuppliersContext, (ctx) => ctx.suppliers);
  const totalRecords = useContextSelector(SuppliersContext, (ctx) => ctx.totalRecords);
  const dataState = useContextSelector(SuppliersContext, (ctx) => ctx.dataState);
  const onDataStateChange = useContextSelector(SuppliersContext, (ctx) => ctx.onDataStateChange);
  const isLoading = useContextSelector(SuppliersContext, (ctx) => ctx.isLoading);
  const searchValue = useContextSelector(SuppliersContext, (ctx) => ctx.searchValue);
  const viewSupplierColumns = useSupplierColumns();
  const { width } = useWindowSize();
  const shouldLockColumns = width >= MANAGE_SUPPLIERS_LAPTOP_MIN_WIDTH_PX;

  const columns = useMemo(() => {
    let columns: GridColumn<ManageSupplier>[] = shouldLockColumns
      ? viewSupplierColumns
      : viewSupplierColumns.map((column) =>
          column.locked ? { ...column, locked: false } : column
        );

    const lastStickyColumnIndex = columns.reduceRight((acc, column, index) => {
      if (acc === -1 && column.locked && column.show) {
        return index;
      }
      return acc;
    }, -1);

    columns = columns.map((column, index) => {
      if (lastStickyColumnIndex === index) {
        return {
          ...column,
          headerClassName: `last-sticky-cell-divider ${column.headerClassName || ''}`.trim(),
          className: `last-sticky-cell-divider ${column.className || ''}`.trim(),
        };
      }

      return column;
    });

    return columns;
  }, [shouldLockColumns, viewSupplierColumns]);

  const gridToolbarChildren = useMemo(() => <SuppliersToolbarActions />, []);

  const rowRender = useCallback(
    (trElement: ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
      const status = (props.dataItem as ManageSupplier | undefined)?.status;
      if (status !== 'inactive' && status !== 'archived') {
        return trElement;
      }

      return cloneElement(trElement, {
        ...trElement.props,
        className: `${
          trElement.props.className ?? ''
        } manage-suppliers-table__row--inactive-status`.trim(),
      });
    },
    []
  );

  const hasSearchValue = searchValue && searchValue.trim();

  return (
    <main className="flex h-full min-h-0 flex-col px-2 pb-4 sm:px-4">
      {hasSearchValue && (
        <div className="mb-2 shrink-0 truncate text-sm font-[600]">
          {t('Showing results for')} &quot;{searchValue.trim()}&quot;
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-hidden">
        <GridWrapper
          id="manageSuppliersTable"
          className="manage-suppliers-table h-full min-h-0"
          columns={columns}
          dataSource={suppliers}
          total={totalRecords}
          dataState={dataState}
          onDataStateChange={onDataStateChange}
          height="100%"
          gridToolbarChildren={gridToolbarChildren}
          rowRender={rowRender}
          {...defaultProps}
          loading={isLoading}
          noRecordText={t('No suppliers found.')}
          scrollable="scrollable"
        />
      </div>
    </main>
  );
};

export default SuppliersTable;
